import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  XCircle,
  ExternalLink,
  Crown
} from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';

export function SubscriptionDetails() {
  const { 
    subscription, 
    stripeSubscription, 
    loading, 
    error, 
    refresh 
  } = useSubscription();
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Only monthly and annual subscriptions are supported

  // Check if user has active subscription (including lifetime)
  const hasActiveSubscription = stripeSubscription?.subscription_status === 'active' || 
                               stripeSubscription?.subscription_status === 'trialing';

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      setCancelError(null);

      // Call the Supabase Edge Function to get customer portal URL
      const { data, error: functionError } = await supabase.functions.invoke('create-customer-portal-session', {
        body: {}
      });

      if (functionError) {
        throw new Error(functionError.message || 'Failed to access customer portal');
      }

      if (data?.url) {
        // Open the Stripe Customer Portal in a new tab
        window.open(data.url, '_blank');
      } else {
        throw new Error('No portal URL received');
      }
    } catch (err) {
      console.error('Error accessing customer portal:', err);
      setCancelError(err instanceof Error ? err.message : 'Failed to access customer portal');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleManageSubscription = () => {
    handleCancelSubscription();
  };

  // Reset confirm state if user navigates away
  useEffect(() => {
    return () => {
      setConfirmCancel(false);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-electric" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="bg-sand/10 p-4 rounded-lg">
        <p className="text-slate">No subscription information available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cancelSuccess && (
        <div className="bg-emerald/10 text-emerald p-4 rounded-lg flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          <span>Your subscription has been canceled successfully. You will continue to have access until the end of your current billing period.</span>
        </div>
      )}

      {cancelError && (
        <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{cancelError}</span>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-sand/20 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <CreditCard className="h-5 w-5 text-midnight" />
          <h2 className="text-lg font-semibold text-midnight">Subscription Details</h2>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-slate">Plan</span>
            <span className="font-medium text-midnight flex items-center">
              {(
                subscription.tier?.name || 'Basic'
              )}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-slate">Status</span>
            <span className={`font-medium ${subscription.active ? 'text-emerald' : 'text-crimson'}`}>
              {subscription.active ? 'Active' : 'Inactive'}
            </span>
          </div>

          {stripeSubscription && (
            <>
              {stripeSubscription.subscription_id && (
                <div className="flex items-center justify-between">
                  <span className="text-slate">Subscription ID</span>
                  <span className="font-medium text-midnight">{stripeSubscription.subscription_id.substring(0, 8)}...</span>
                </div>
              )}

              {stripeSubscription.payment_method_brand && stripeSubscription.payment_method_last4 && (
                <div className="flex items-center justify-between">
                  <span className="text-slate">Payment Method</span>
                  <span className="font-medium text-midnight">
                    {stripeSubscription.payment_method_brand.charAt(0).toUpperCase() + 
                     stripeSubscription.payment_method_brand.slice(1)} •••• {stripeSubscription.payment_method_last4}
                  </span>
                </div>
              )}

              {stripeSubscription.current_period_end && (
                <div className="flex items-center justify-between">
                  <span className="text-slate">Current Period Ends</span>
                  <span className="font-medium text-midnight">
                    {format(new Date(stripeSubscription.current_period_end * 1000), 'MMMM d, yyyy')}
                  </span>
                </div>
              )}

              {stripeSubscription.cancel_at_period_end && (
                <div className="bg-gold/10 p-4 rounded-lg mt-4">
                  <div className="flex items-start">
                    <Calendar className="h-5 w-5 text-gold mr-2 mt-0.5" />
                    <div>
                      <p className="font-medium text-midnight">Your subscription will end on {
                        stripeSubscription.current_period_end 
                          ? format(new Date(stripeSubscription.current_period_end * 1000), 'MMMM d, yyyy')
                          : 'the end of your current billing period'
                      }</p>
                      <p className="text-sm text-slate mt-1">
                        You will continue to have access to all premium features until this date.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {subscription.expires_at && !stripeSubscription?.subscription_id && (
            <div className="flex items-center justify-between">
              <span className="text-slate">Expires</span>
              <span className="font-medium text-midnight">
                {format(new Date(subscription.expires_at), 'MMMM d, yyyy')}
              </span>
            </div>
          )}
        </div>

        {/* Manage Subscription Button */}
        {stripeSubscription?.subscription_id && (
          <div className="mt-6 pt-6 border-t border-sand/20">
            <h3 className="text-md font-medium text-midnight mb-4">Subscription Management</h3>

            <button
              onClick={handleCancelSubscription}
              className={`flex items-center justify-center px-4 py-3 ${
                cancelLoading
                  ? 'bg-slate/20 text-slate cursor-not-allowed'
                  : 'bg-electric text-white hover:bg-electric/90'
              } rounded-lg transition-colors w-full sm:w-auto`}
              disabled={cancelLoading}
            >
              <ExternalLink className="h-5 w-5 mr-2" />
              {cancelLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Redirecting...
                </>
              ) : (
                <>
                  Manage Subscription
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}